import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

/* Screen D — read history. Sorted newest first (backend already does this).
 * Only shows the number here per §9 ("It is an intake artifact and nothing
 * else") — this list IS an intake artifact, so surfacing it here is allowed.
 */

const BAND_STYLE = {
  tuneup:     { bg: "#F5F3EC", fg: "#16293b", label: "Tune-up" },
  soft_spots: { bg: "#F5F3EC", fg: "#16293b", label: "Solid frame, soft spots" },
  real_work:  { bg: "#F5F3EC", fg: "#16293b", label: "Real work ahead" },
  long_haul:  { bg: "#F5F3EC", fg: "#16293b", label: "Long haul" },
};

export default function IntakeReads() {
  const [reads, setReads] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/intake-reads");
        setReads(r.data.intake_reads || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-8 py-14">
        <button
          className="mono-caps text-slate-500 hover:text-slate-800 mb-8"
          onClick={() => nav("/dashboard")}
          data-testid="intake-reads-back"
        >
          ← Dashboard
        </button>

        <div className="flex items-start justify-between gap-6 mb-10">
          <div>
            <div className="mono-caps text-slate-500 mb-3">Intake read history</div>
            <h1 className="font-display text-4xl sm:text-5xl" style={{ color: "#16293b" }}>
              Prior reads
            </h1>
          </div>
          <Link to="/intake-read" className="btn-primary" data-testid="intake-reads-new">
            New intake read
          </Link>
        </div>

        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : reads.length === 0 ? (
          <div className="border border-slate-200 bg-white p-8" data-testid="intake-reads-empty">
            <div className="text-slate-500 mb-6">No intake reads yet.</div>
            <Link to="/intake-read" className="btn-primary">Start one</Link>
          </div>
        ) : (
          <div className="border border-slate-200 bg-white" data-testid="intake-reads-table">
            <div
              className="grid gap-4 px-6 py-3 mono-caps text-slate-500 text-[0.65rem] border-b border-slate-200"
              style={{ gridTemplateColumns: "1fr auto auto auto auto" }}
            >
              <div>Idea name</div>
              <div>Read</div>
              <div>Band</div>
              <div>Date</div>
              <div>Action</div>
            </div>
            {reads.map((r) => {
              const band = BAND_STYLE[r.band?.key] || BAND_STYLE.long_haul;
              return (
                <div
                  key={r.id}
                  className="grid gap-4 px-6 py-4 items-center border-b border-slate-100 last:border-b-0"
                  style={{ gridTemplateColumns: "1fr auto auto auto auto" }}
                  data-testid={`intake-read-row-${r.id}`}
                >
                  <div className="text-[15px]" style={{ color: "#16293b" }}>
                    <Link
                      to={`/intake-read/${r.id}`}
                      className="hover:underline"
                      data-testid={`intake-read-open-${r.id}`}
                    >
                      {r.idea_name}
                    </Link>
                  </div>
                  <div className="font-display" style={{ fontSize: "20px", color: "#16293b", lineHeight: 1 }}>
                    {r.total_read}
                  </div>
                  <div
                    className="mono-caps text-[11px] px-2 py-1"
                    style={{ background: band.bg, color: band.fg }}
                  >
                    {band.label}
                  </div>
                  <div className="mono-caps text-slate-400 text-[11px]">
                    {new Date(r.created_at).toLocaleDateString()}
                  </div>
                  <Link
                    to={`/intake-read/${r.id}`}
                    className="mono-caps text-slate-600 hover:text-slate-900 text-[11px]"
                  >
                    Open →
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
